import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, X, Upload, Video, Package } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProductMediaItem({ media, onDelete, getMediaUrl }) {
  return (
    <div className="relative w-14 h-14 group">
      {media.media_type === "video" ? (
        <div className="w-full h-full bg-velvet flex items-center justify-center">
          <Video size={16} className="text-cream/50" />
        </div>
      ) : (
        <img
          src={getMediaUrl(media.media_path)}
          alt=""
          className="w-full h-full object-cover"
        />
      )}
      <button
        onClick={() => onDelete(media.id)}
        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
      >
        <X size={12} />
      </button>
    </div>
  );
}

function ProductItem({ product, onEdit, onDelete, onUpload, onDeleteMedia, getMediaUrl, uploading }) {
  const primaryMedia = product.media && product.media.length > 0 ? product.media[0] : null;
  
  return (
    <div
      data-testid={`product-item-${product.id}`}
      className="bg-charcoal/50 border border-royal-gold/10 overflow-hidden hover:border-royal-gold/30 transition-colors"
    >
      <div className="relative aspect-square bg-velvet">
        {primaryMedia ? (
          primaryMedia.media_type === "video" ? (
            <video
              src={getMediaUrl(primaryMedia.media_path)}
              className="w-full h-full object-cover"
              muted
            />
          ) : (
            <img
              src={getMediaUrl(primaryMedia.media_path)}
              alt={product.title}
              className="w-full h-full object-cover"
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-cream/20">
            <Package size={48} />
          </div>
        )}
        
        {product.media && product.media.length > 1 && (
          <span className="absolute top-2 right-2 bg-deep-black/80 text-cream text-xs px-2 py-1">
            +{product.media.length - 1}
          </span>
        )}

        {product.is_featured && (
          <span className="absolute top-2 left-2 bg-royal-gold text-deep-black text-xs px-2 py-1 font-display tracking-wider uppercase">
            Featured
          </span>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-body font-medium text-cream">{product.title}</h3>
            <p className="font-body text-xs text-cream/50 capitalize">{product.category}</p>
          </div>
          <p className="font-body text-sm text-royal-gold">₹{product.price}</p>
        </div>
        <p className="font-body text-xs text-cream/40 line-clamp-2 mb-4">
          {product.description}
        </p>

        {product.media && product.media.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {product.media.slice(0, 4).map((media) => (
              <ProductMediaItem 
                key={media.id} 
                media={media} 
                onDelete={onDeleteMedia}
                getMediaUrl={getMediaUrl}
              />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="flex-1">
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => onUpload(product.id, e)}
              disabled={uploading}
            />
            <span className="flex items-center justify-center space-x-1 px-3 py-2 bg-velvet text-cream/60 text-xs hover:text-cream cursor-pointer transition-colors">
              <Upload size={14} />
              <span>Upload</span>
            </span>
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(product)}
            data-testid={`edit-product-${product.id}`}
            className="flex items-center space-x-1 border-royal-gold/30 text-cream hover:bg-royal-gold/10"
          >
            <Pencil size={14} />
            <span>Edit</span>
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => onDelete(product.id)}
            data-testid={`delete-product-${product.id}`}
            className="flex items-center space-x-1"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}

const AdminProducts = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("retail");
  const [isFeatured, setIsFeatured] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [token]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (product) => {
    if (product) {
      setEditingProduct(product);
      setTitle(product.title);
      setDescription(product.description);
      setPrice(product.price.toString());
      setCategory(product.category);
      setIsFeatured(product.is_featured);
    } else {
      setEditingProduct(null);
      setTitle("");
      setDescription("");
      setPrice("");
      setCategory("retail");
      setIsFeatured(false);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      title: title,
      description: description,
      price: parseFloat(price) || 0,
      category: category,
      is_featured: isFeatured,
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Product created successfully");
      }
      handleCloseDialog();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleFileUpload = async (productId, event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fd = new FormData();
      fd.append("file", file);

      try {
        await axios.post(`${API}/products/${productId}/media`, fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    toast.success("Media uploaded successfully");
    setUploading(false);
    fetchProducts();
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm("Delete this media?")) return;

    try {
      await axios.delete(`${API}/product-media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Media deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete media");
    }
  };

  const getMediaUrl = (mediaPath) => {
    if (!mediaPath) return null;
    if (mediaPath.startsWith("http")) return mediaPath;
    return `${BACKEND_URL}${mediaPath}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-royal-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-display text-xs tracking-[0.3em] uppercase text-royal-gold mb-2">Inventory</p>
          <h1 className="font-serif text-3xl text-cream">Products</h1>
        </div>
        <Button
          onClick={() => handleOpenDialog(null)}
          data-testid="add-product-btn"
          className="btn-luxury flex items-center space-x-2 py-3"
        >
          <Plus size={18} />
          <span>Add Product</span>
        </Button>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-20 bg-charcoal/30 border border-royal-gold/10">
          <Package size={60} className="mx-auto mb-4 text-cream/20" />
          <h3 className="font-serif text-xl text-cream mb-2">No Products Yet</h3>
          <p className="font-body text-sm text-cream/50 mb-6">
            Start by adding your first product
          </p>
          <Button onClick={() => handleOpenDialog(null)} className="btn-luxury">
            Add Product
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <ProductItem
              key={product.id}
              product={product}
              onEdit={handleOpenDialog}
              onDelete={handleDelete}
              onUpload={handleFileUpload}
              onDeleteMedia={handleDeleteMedia}
              getMediaUrl={getMediaUrl}
              uploading={uploading}
            />
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-charcoal border-royal-gold/20">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-cream">
              {editingProduct ? "Edit Product" : "Add Product"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="font-display text-xs tracking-widest uppercase text-cream/70">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="product-title-input"
                className="bg-velvet border-royal-gold/20 text-cream"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="font-display text-xs tracking-widest uppercase text-cream/70">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="product-description-input"
                rows={3}
                className="bg-velvet border-royal-gold/20 text-cream resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price" className="font-display text-xs tracking-widest uppercase text-cream/70">Price (₹)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  data-testid="product-price-input"
                  className="bg-velvet border-royal-gold/20 text-cream"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-display text-xs tracking-widest uppercase text-cream/70">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="product-category-select" className="bg-velvet border-royal-gold/20 text-cream">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-royal-gold/20">
                    <SelectItem value="retail" className="text-cream">Retail</SelectItem>
                    <SelectItem value="wholesale" className="text-cream">Wholesale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <Label htmlFor="featured" className="font-display text-xs tracking-widest uppercase text-cream/70">Featured Product</Label>
              <Switch
                id="featured"
                checked={isFeatured}
                onCheckedChange={setIsFeatured}
                data-testid="product-featured-switch"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-cream/20 text-cream hover:bg-cream/10">
                Cancel
              </Button>
              <Button type="submit" className="btn-luxury py-2" data-testid="save-product-btn">
                {editingProduct ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
